import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react'
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from 'motion/react'
import { pushOverlay, popOverlay, isTopOverlay } from './overlayStack'
import { fokussierbare, haltTab } from './focusTrap'
import { createPortal } from 'react-dom'

// "Feder" motion and section stagger — DESIGN.md §6.
const DRAWER_EASE = [0.23, 1, 0.32, 1] as const
const SHEET_SPRING = { type: 'spring', stiffness: 120, damping: 20, mass: 1 } as const
const EXIT_TRANSITION = { duration: 0.22, ease: DRAWER_EASE }
const SCRIM_ENTER_TRANSITION = { duration: 0.3, ease: DRAWER_EASE }
const REDUCED_MOTION_TRANSITION = { duration: 0.12, ease: 'easeOut' } as const
// Calm variant (QM desk pages, owner D29/D30/D34): the sheet and the scrim fade in over 180 ms and nothing else moves - no slide,
// no blur, no stagger. Closing is immediate. With reduced motion nothing animates at all.
const CALM_FADE = { duration: 0.18, ease: 'easeOut' } as const
const CALM_NONE = { duration: 0 } as const
const BACKDROP_INITIAL = { backdropFilter: 'blur(0px)', WebkitBackdropFilter: 'blur(0px)' }
const BACKDROP_ANIMATE = {
  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', transition: SCRIM_ENTER_TRANSITION,
}
const BACKDROP_EXIT = { ...BACKDROP_INITIAL, transition: EXIT_TRANSITION }
// One keyframe at offset 0: the end state is the section's own style, so its own
// opacity or transform is never overridden; `translate` composes with `transform`.
// (A lone keyframe WITHOUT an offset would be the end keyframe and fade sections out.)
const SECTION_KEYFRAMES = [{ offset: 0, opacity: 0, translate: '0 8px' }]
const SECTION_TIMING = { duration: 240, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'backwards' } as const
const SECTION_START_DELAY = 120
const SECTION_STAGGER = 55
const SECTION_LAST_STAGGER_INDEX = 5

function animateSections(body: HTMLElement): Animation[] {
  const marked = body.querySelector('[data-drawer-sections]')
  let container: Element = marked ?? body
  // Prefer the first marked container; otherwise unwrap single-child containers.
  if (!marked) {
    while (container.children.length === 1) container = container.children[0]
  }
  // Nested marked children contribute their sections in document order.
  const sections = (parent: Element): Element[] => Array.from(parent.children).flatMap(child =>
    child.hasAttribute('data-drawer-sections') ? sections(child) : [child],
  )
  return sections(container).flatMap((el, i) => typeof el.animate === 'function' ? [
    el.animate(SECTION_KEYFRAMES, {
      ...SECTION_TIMING,
      delay: SECTION_START_DELAY + Math.min(i, SECTION_LAST_STAGGER_INDEX) * SECTION_STAGGER,
    }),
  ] : [])
}

// The scrim stays mounted for the 220 ms exit; only a present scrim may close,
// otherwise a click on the leaving one would call onClose a second time.
function Scrim({ onClose, calm, reducedMotion }: { onClose: () => void; calm?: boolean; reducedMotion?: boolean | null }) {
  const isPresent = useIsPresent()
  return (
    <motion.div
      className="absolute inset-0 bg-slate-950/40"
      aria-hidden
      onClick={isPresent ? onClose : undefined}
      initial={calm && reducedMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1, transition: calm ? (reducedMotion ? CALM_NONE : CALM_FADE) : SCRIM_ENTER_TRANSITION }}
      exit={{ opacity: 0, transition: calm ? CALM_NONE : EXIT_TRANSITION }}
    />
  )
}

// Staggers once per mounted sheet (one AnimatePresence entrance), not per `open`
// flip: reopening during the exit revives the same sheet and must not blank its
// sections again, and closing mid-stagger lets the running animations finish.
function DrawerBody({ children, reducedMotion, calm }: { children: ReactNode; reducedMotion: boolean | null; calm?: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (reducedMotion || calm || !ref.current) return
    const animations = animateSections(ref.current)
    return () => { animations.forEach(animation => animation.cancel()) }
  }, [reducedMotion, calm])
  return (
    <div ref={ref} data-drawer-body className={calm
      ? 'px-4 sm:px-6 py-5 overflow-y-auto flex-1'
      : 'px-4 sm:px-6 py-5 overflow-y-auto flex-1 bg-white/85 dark:bg-slate-900/85 mx-2 sm:mx-3 my-3 rounded-2xl border border-white/20 dark:border-white/10'}>
      {children}
    </div>
  )
}

interface DrawerProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  subtitle?: ReactNode
  /** Right-aligned header action buttons. */
  actions?: ReactNode
  /** Optional full-width row at the bottom of the header, under title and actions (e.g. a progress bar). */
  headerBelow?: ReactNode
  children: ReactNode
  /** Footer row for primary/destructive actions. */
  footer?: ReactNode
  /** Width preset. `md` = 480px, `lg` = 640px (default), `xl` = 800px, `2xl` = 1040px. */
  width?: 'md' | 'lg' | 'xl' | '2xl'
  /**
   * Calm variant for QM desk pages (owner D29/D30/D34, prototype Leitung-Kalender): a solid 480 px sheet (the `width` preset is ignored),
   * a 180 ms opacity fade as the ONLY motion - no slide, no blur, no section stagger - and an immediate close.
   */
  calm?: boolean
}

// Full-width sheet on phones, fixed presets from `sm` up.
const WIDTHS: Record<NonNullable<DrawerProps['width']>, string> = {
  md: 'w-full sm:w-[480px]',
  lg: 'w-full sm:w-[640px]',
  xl: 'w-full sm:w-[800px]',
  '2xl': 'w-full sm:w-[1040px]',
}

/**
 * Right-anchored Liquid Glass drawer — DESIGN.md §14.
 *
 * Use for entity detail views (user profile, role detail, asset detail)
 * where the drawer should NOT take the user out of the list context.
 * Anatomy: dimmed + blurred backdrop (left), glass sheet (right), optional
 * header + footer. Escape + backdrop click close.
 *
 * Differs from <Modal> in three ways:
 *   1. Anchored right, takes full viewport height
 *   2. Bigger width preset for dense entity profiles
 *   3. "Feder" spring entrance from the right, eased exit, and entrance-only section stagger.
 *      Mark section containers with data-drawer-sections; reduced motion uses a sheet fade only.
 */
export function Drawer({ open, onClose, title, subtitle, actions, headerBelow, children, footer, width = 'lg', calm = false }: DrawerProps) {
  const reducedMotion = useReducedMotion()
  // Der NEUESTE onClose in einer Referenz (wie in Modal.tsx): der Drawer wird von Eltern mit einer neuen Pfeilfunktion je
  // Render gefüttert (SchulungenTab bei jedem Abruf der Liste). Stünde onClose in den Abhängigkeiten, würde der Drawer
  // dabei im overlayStack wieder ÜBER die offenen Modals rücken, und Escape schlösse ihn samt Assistent statt des obersten.
  const onCloseRef = useRef(onClose)
  useLayoutEffect(() => { onCloseRef.current = onClose })
  const sheetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const overlayId = Symbol('drawer')
    pushOverlay(overlayId)
    // Das Blatt steht im selben Commit im DOM; das Element hier festhalten, denn beim Abmelden ist die Referenz schon leer.
    const sheet = sheetRef.current
    // Tab bleibt im Drawer, solange er oben liegt: ein Modal, ein offenes Menü oder eine Auswahl darüber (eigener Fokus,
    // Portal) macht ihn nicht zum obersten Overlay, dann fasst er Tab nicht an.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isTopOverlay(overlayId)) onCloseRef.current()
      else if (e.key === 'Tab' && sheet && isTopOverlay(overlayId)) haltTab(sheet, e)
    }
    document.addEventListener('keydown', onKey)
    // Fokus wie in Modal.tsx: hinein beim Öffnen (erstes Bedienelement im Inhalt, sonst das Blatt selbst), beim Schließen
    // zurück dorthin, wo er vorher war. preventScroll: das Blatt fährt noch von rechts ein.
    const vorher = document.activeElement as HTMLElement | null
    // Hat schon etwas im Blatt den Fokus (autoFocus des Inhalts), bleibt er dort.
    if (sheet && !sheet.contains(document.activeElement)) {
      const body = sheet.querySelector<HTMLElement>('[data-drawer-body]')
      const ziel = (body ? fokussierbare(body)[0] : undefined) ?? sheet
      ziel.focus({ preventScroll: true })
    }
    // Der Body-Scroll wird im overlayStack gezählt (pushOverlay/popOverlay), nicht je Drawer.
    return () => {
      popOverlay(overlayId)
      document.removeEventListener('keydown', onKey)
      // Nur zurückgeben, wenn der Fokus nicht schon bewusst woanders hin ist (Link im Drawer, Navigation, Modal darüber
      // hat seinen eigenen Rückweg).
      const jetzt = document.activeElement
      if (vorher?.isConnected && (!jetzt || jetzt === document.body || sheet?.contains(jetzt))) vorher.focus?.({ preventScroll: true })
    }
  }, [open])

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="drawer"
          className="fixed inset-0 z-[60] flex justify-end"
          initial={calm ? false : BACKDROP_INITIAL}
          animate={calm ? undefined : BACKDROP_ANIMATE}
          exit={calm ? undefined : BACKDROP_EXIT}
        >
          <Scrim onClose={onClose} calm={calm} reducedMotion={reducedMotion} />
          <motion.div
            ref={sheetRef}
            tabIndex={-1}
            className={calm
              ? `bg-surface-container-lowest shadow-[0_8px_32px_rgba(0,0,0,0.18),inset_1px_0_0_var(--color-outline-variant)] ${WIDTHS.md} max-w-full h-full flex flex-col relative rounded-none outline-none`
              : `liquid-glass ${WIDTHS[width]} max-w-full sm:max-w-[92vw] h-full flex flex-col relative rounded-none sm:rounded-l-3xl outline-none`}
            initial={calm ? (reducedMotion ? false : { opacity: 0 }) : reducedMotion ? { opacity: 0 } : { x: '100%' }}
            animate={calm
              ? { opacity: 1, transition: reducedMotion ? CALM_NONE : CALM_FADE }
              : reducedMotion
                ? { opacity: 1, transition: REDUCED_MOTION_TRANSITION }
                : { x: 0, transition: SHEET_SPRING }}
            exit={calm
              ? { opacity: 0, transition: CALM_NONE }
              : reducedMotion
                ? { opacity: 0, transition: REDUCED_MOTION_TRANSITION }
                : { x: '100%', transition: EXIT_TRANSITION }}
          >
            {(title || subtitle || actions || headerBelow) && (
              <header className={`px-4 sm:px-6 py-4 border-b ${calm ? 'border-surface-container' : 'border-white/20'} flex items-start justify-between gap-4 flex-shrink-0${headerBelow ? ' flex-wrap' : ''}`}>
                <div className="min-w-0 flex-1">
                  {title && <h2 className={calm ? 'text-base font-bold text-on-surface m-0' : 'text-lg font-semibold text-on-surface tracking-tight font-headline'}>{title}</h2>}
                  {subtitle && <p className={calm ? 'text-sm text-on-surface-variant mt-0.5 m-0' : 'text-xs text-on-surface-variant mt-0.5'}>{subtitle}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {actions}
                  <button onClick={onClose} aria-label="Schließen"
                    className="p-1.5 rounded-lg text-on-surface-variant hover:text-on-surface hover:bg-white/40 dark:hover:bg-white/10 transition-colors">
                    <span className="material-symbols-outlined !text-xl">close</span>
                  </button>
                </div>
                {headerBelow && <div className="basis-full min-w-0">{headerBelow}</div>}
              </header>
            )}
            <DrawerBody reducedMotion={reducedMotion} calm={calm}>{children}</DrawerBody>
            {footer && (
              <footer className={`px-6 py-4 border-t ${calm ? 'border-surface-container' : 'border-white/20'} flex justify-end gap-2 flex-shrink-0`}>
                {footer}
              </footer>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
